import { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import MenuBook from '@mui/icons-material/MenuBook';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import PictureAsPdf from '@mui/icons-material/PictureAsPdf';
import ImageOutlined from '@mui/icons-material/ImageOutlined';
import ExpandMore from '@mui/icons-material/ExpandMore';
import type { RiferimentoAr } from '../../types/ApparecchiArTypes';
import { caricaRiferimento, togliRiferimento } from '../../services/apparecchiArService';

/**
 * I documenti di consultazione di un apparecchio: manuali, schemi, tavole.
 *
 * ⚠️ **Non sono superfici, e la differenza non è di parole.** Una fotografia di
 * superficie è un BERSAGLIO: ARCore la usa per agganciare l'apparecchio in cantiere. Un
 * documento di consultazione serve ai modelli per sapere che aspetto ha un DC SWITCH
 * prima di cercarlo. Se i due si mescolassero, il sistema proverebbe ad agganciare una
 * pagina stampata sopra un inverter — e fallirebbe senza dire perché.
 *
 * ⚠️ **La descrizione entra nel messaggio ai modelli**, non è un'etichetta d'archivio.
 * «Tavola 5-1: schema di collegamento con i morsetti numerati» orienta la lettura;
 * «manuale.pdf» non dice niente a nessuno, e il modello deve indovinare da solo cosa
 * sta guardando.
 */

interface Props {
  apparecchioId: string;
  riferimenti: RiferimentoAr[];
  onCambio: (r: RiferimentoAr[]) => void;
}

export default function Riferimenti({ apparecchioId, riferimenti, onCambio }: Props) {
  const [aperto, setAperto] = useState(riferimenti.length > 0);
  const [inCorso, setInCorso] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  /*
    ⚠️ **Ogni file ha la SUA descrizione, e per questo la coda esiste.** La riga di
    descrizione entra nel messaggio ai modelli: una descrizione sola per otto tavole del
    manuale non orienta niente. Perciò i file scelti aspettano in una coda finché non si
    è detto cosa sono, invece di partire subito.
  */
  const [coda, setCoda] = useState<Array<{ file: File; descrizione: string }>>([]);
  const [suGoccia, setSuGoccia] = useState(false);
  /*
    ⚠️ Il nome dell'insieme si scrive UNA volta per raffica, non per file. Chi carica
    tre pagine di uno schema le sta pensando come una cosa: chiedergli tre volte cosa
    sono lo porta a scrivere tre volte la stessa riga, oppure a lasciarle vuote.
  */
  const [gruppo, setGruppo] = useState('');
  const campo = useRef<HTMLInputElement | null>(null);

  const accoda = (f: FileList | null) => {
    if (!f?.length) return;
    setErrore(null);
    setCoda((c) => [...c, ...Array.from(f).map((file) => ({ file, descrizione: '' }))]);
  };

  const carica = async () => {
    if (coda.length === 0) return;
    setInCorso(true);
    setErrore(null);
    /*
      ⚠️ Uno alla volta, non in parallelo. Sei PDF da venti megabyte insieme saturano la
      banda e fanno scadere i caricamenti a metà: quello che si guadagna in secondi si
      perde in tentativi ripetuti. E così `riferimenti` cresce in modo coerente a ogni
      passo, invece di partire sei volte dallo stesso elenco.
    */
    let attuali = riferimenti;
    try {
      const nomeGruppo = gruppo.trim() || (coda.length > 1 ? 'caricati insieme' : '');
      for (let i = 0; i < coda.length; i++) {
        const { file, descrizione } = coda[i];
        const nuovo = await caricaRiferimento(
          apparecchioId,
          file,
          descrizione.trim(),
          attuali,
          nomeGruppo || undefined,
          coda.length > 1 ? i + 1 : undefined,
        );
        attuali = [...attuali, nuovo];
        onCambio(attuali);
      }
      setCoda([]);
      setGruppo('');
      if (campo.current) campo.current.value = '';
    } catch (e) {
      setErrore(e instanceof Error ? e.message : 'il caricamento non è riuscito');
    } finally {
      setInCorso(false);
    }
  };

  return (
    <Box sx={{ mt: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        onClick={() => setAperto((v) => !v)}
        sx={{ px: 1.5, py: 1, cursor: 'pointer' }}
      >
        <MenuBook sx={{ fontSize: 18, color: 'text.secondary' }} />
        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Documenti di consultazione</Typography>
        <Chip size="small" label={riferimenti.length} sx={{ height: 18, fontSize: 11 }} />
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', md: 'block' } }}>
          manuali e schemi che i modelli leggono prima di proporre
        </Typography>
        <ExpandMore
          sx={{
            fontSize: 20,
            color: 'text.secondary',
            transform: aperto ? 'rotate(180deg)' : 'none',
            transition: 'transform .15s',
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          }}
        />
      </Stack>

      <Collapse in={aperto}>
        <Box sx={{ px: 1.5, pb: 1.5 }}>
          {riferimenti.length > 0 && (
            <Stack spacing={0.5} sx={{ mb: 1.5 }}>
              {riferimenti.map((r) => (
                <Stack
                  key={r.id}
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ py: 0.5, px: 1, bgcolor: 'action.hover', borderRadius: 1 }}
                >
                  {r.tipo === 'application/pdf' ? (
                    <PictureAsPdf sx={{ fontSize: 17, color: 'text.secondary' }} />
                  ) : (
                    <ImageOutlined sx={{ fontSize: 17, color: 'text.secondary' }} />
                  )}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 500 }} noWrap>
                      {r.nome}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap display="block">
                      {r.gruppo && (
                        <Box component="span" sx={{ color: 'primary.main', mr: 0.5 }}>
                          [{r.gruppo}
                          {r.ordine ? ` · ${r.ordine}` : ''}]
                        </Box>
                      )}
                      {r.descrizione || 'senza descrizione — i modelli devono indovinare cos’è'}
                    </Typography>
                  </Box>
                  <Typography
                    variant="caption"
                    color="text.disabled"
                    sx={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    {(r.byte / 1024 / 1024).toFixed(1)} MB
                  </Typography>
                  <DeleteOutline
                    onClick={async () => {
                      await togliRiferimento(apparecchioId, r, riferimenti);
                      onCambio(riferimenti.filter((x) => x.id !== r.id));
                    }}
                    titleAccess="Togli questo documento"
                    sx={{
                      fontSize: 17,
                      color: 'text.disabled',
                      cursor: 'pointer',
                      '&:hover': { color: 'error.main' },
                    }}
                  />
                </Stack>
              ))}
            </Stack>
          )}

          <Box
            onDragOver={(e) => {
              e.preventDefault();
              setSuGoccia(true);
            }}
            onDragLeave={() => setSuGoccia(false)}
            onDrop={(e) => {
              e.preventDefault();
              setSuGoccia(false);
              accoda(e.dataTransfer.files);
            }}
            sx={{
              border: '1px dashed',
              borderColor: suGoccia ? 'primary.main' : 'divider',
              bgcolor: suGoccia ? 'action.hover' : 'transparent',
              borderRadius: 1.5,
              p: coda.length ? 1.25 : 2,
              transition: 'border-color .12s, background-color .12s',
              '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
            }}
          >
            {coda.length === 0 ? (
              <Stack alignItems="center" spacing={0.75}>
                <Typography variant="body2" color="text.secondary">
                  Trascina qui manuali e schemi, anche più d'uno
                </Typography>
                <Button component="label" size="small" variant="outlined">
                  oppure scegli i file
                  <input
                    ref={campo}
                    type="file"
                    hidden
                    multiple
                    accept="application/pdf,image/*"
                    onChange={(e) => accoda(e.target.files)}
                  />
                </Button>
              </Stack>
            ) : (
              <Stack spacing={1}>
                {coda.length > 1 && (
                  <TextField
                    size="small"
                    fullWidth
                    value={gruppo}
                    onChange={(e) => setGruppo(e.target.value)}
                    disabled={inCorso}
                    label={`Cosa sono questi ${coda.length} documenti, tutti insieme`}
                    placeholder="Schema di collegamento CC, tre pagine in ordine"
                    helperText="I modelli li leggeranno come un insieme unico, nell'ordine in cui li vedi qui."
                  />
                )}
                {coda.map((v, i) => (
                  <Stack
                    key={`${v.file.name}-${i}`}
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={1}
                    alignItems={{ md: 'center' }}
                  >
                    {v.file.type === 'application/pdf' ? (
                      <PictureAsPdf sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }} />
                    ) : (
                      <ImageOutlined sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }} />
                    )}
                    <Typography
                      sx={{ fontSize: 12.5, minWidth: { md: 190 }, maxWidth: { md: 190 } }}
                      noWrap
                      title={v.file.name}
                    >
                      {coda.length > 1 && (
                        <Box component="span" sx={{ color: 'text.disabled', mr: 0.5 }}>
                          {i + 1}.
                        </Box>
                      )}
                      {v.file.name}
                    </Typography>
                    <TextField
                      size="small"
                      fullWidth
                      value={v.descrizione}
                      onChange={(e) =>
                        setCoda((c) =>
                          c.map((x, j) => (j === i ? { ...x, descrizione: e.target.value } : x)),
                        )
                      }
                      placeholder="Tavola 5-1: schema di collegamento, morsetti numerati"
                      disabled={inCorso}
                    />
                    <Typography
                      variant="caption"
                      color="text.disabled"
                      sx={{ fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}
                    >
                      {(v.file.size / 1024 / 1024).toFixed(1)} MB
                    </Typography>
                    <DeleteOutline
                      onClick={() => setCoda((c) => c.filter((_, j) => j !== i))}
                      titleAccess="Togli dalla coda"
                      sx={{
                        fontSize: 17,
                        color: 'text.disabled',
                        cursor: 'pointer',
                        flexShrink: 0,
                        '&:hover': { color: 'error.main' },
                      }}
                    />
                  </Stack>
                ))}
                <Stack direction="row" spacing={1} alignItems="center" sx={{ pt: 0.5 }}>
                  <Button
                    size="small"
                    variant="contained"
                    disabled={inCorso}
                    onClick={() => void carica()}
                    startIcon={inCorso ? <CircularProgress size={13} /> : undefined}
                  >
                    {inCorso
                      ? `Carico… ${riferimenti.length}/${riferimenti.length + coda.length}`
                      : `Carica ${coda.length === 1 ? 'il file' : `i ${coda.length} file`}`}
                  </Button>
                  <Button component="label" size="small" disabled={inCorso}>
                    aggiungine altri
                    <input
                      type="file"
                      hidden
                      multiple
                      accept="application/pdf,image/*"
                      onChange={(e) => accoda(e.target.files)}
                    />
                  </Button>
                  <Button size="small" disabled={inCorso} onClick={() => setCoda([])}>
                    svuota
                  </Button>
                  {/*
                    ⚠️ Non si BLOCCA il caricamento di un file senza descrizione: a volte
                    il nome basta, e un obbligo qui costringerebbe a inventare una riga
                    pur di andare avanti — che ai modelli è peggio del silenzio. Si dice
                    e basta.
                  */}
                  {coda.some((v) => !v.descrizione.trim()) && (
                    <Typography variant="caption" color="text.secondary">
                      {coda.filter((v) => !v.descrizione.trim()).length} senza descrizione: i
                      modelli dovranno indovinare cosa sono
                    </Typography>
                  )}
                </Stack>
              </Stack>
            )}
          </Box>

          {errore && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {errore}
            </Alert>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            PDF o immagini, fino a 25 MB. Ne vengono letti al massimo sei per proposta —
            oltre, il costo cresce senza che la precisione segua.
          </Typography>
        </Box>
      </Collapse>
    </Box>
  );
}
